import { useState, useRef, useEffect } from "react";
import { Search, ChevronDown } from "lucide-react";

const ROLES = ["Chef de Projet", "Responsable Technique", "Technicien IoT", "Chef de Chantier"];

export default function MemberMultiSelect({ membres, selected, onChange, defaultRole, tagColor = "bg-blue-50 text-blue-700" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState(defaultRole || "");
  const ref = useRef(null);

  // Fermer au clic extérieur
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const available = membres.filter(m => {
    if (selected.some(id => String(id) === String(m._id))) return false;
    if (filterRole && m.role !== filterRole) return false;
    if (search) {
      const s = search.toLowerCase();
      return m.nom.toLowerCase().includes(s) || m.email?.toLowerCase().includes(s);
    }
    return true;
  });

  const add = (id) => {
    onChange([...selected, id]);
  };

  const remove = (id) => {
    onChange(selected.filter(x => x !== id));
  };

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        <span className={selected.length === 0 ? "text-slate-400" : "text-slate-700"}>
          {selected.length === 0 ? "Ajouter un membre" : `${selected.length} membre${selected.length > 1 ? "s" : ""} sélectionné${selected.length > 1 ? "s" : ""}`}
        </span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Tags */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map(id => {
            const m = membres.find(x => String(x._id) === String(id));
            return (
              <span key={id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${tagColor}`}>
                {m?.nom || id}
                <button type="button" onClick={() => remove(id)} className="hover:text-red-500">&times;</button>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {/* Filtre rôle + Recherche */}
          <div className="p-2 border-b border-gray-100 space-y-1.5">
            <div className="flex flex-wrap gap-1">
              {["", ...ROLES].map(role => (
                <button
                  key={role}
                  type="button"
                  onClick={() => setFilterRole(role)}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors ${
                    filterRole === role
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-slate-600 hover:bg-gray-200"
                  }`}
                >
                  {role || "Tous"}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher..."
                autoFocus
                className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Liste */}
          <div className="max-h-48 overflow-y-auto">
            {available.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">Aucun membre disponible</p>
            ) : (
              available.map(m => (
                <button
                  key={m._id}
                  type="button"
                  onClick={() => add(m._id)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-blue-50 transition-colors text-left"
                >
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{m.nom}</p>
                    <p className="text-[11px] text-slate-400">{m.role}{m.email ? ` · ${m.email}` : ''}</p>
                  </div>
                  <span className="text-blue-500 text-lg leading-none">+</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
